import { useMemo } from 'react';

import { useIntl } from 'react-intl';

import type { IncidentsTimesSectionData } from '@src/models/interfaces/executionsHistoryDashboard.interface';
import type { ReportTone, SectionStatus, TimeMetricItem } from '@src/packages/dashboard/components/ReportSummary';

import { formatCompactDuration, getDelayMetrics, getExtraTimeMetrics } from '../historySelectors';

type MetricDefinition = {
  key: string;
  value: string | number;
  values?: Record<string, number>;
};

const EMPTY_VALUE = '—';

const minutesValue = (minutes: number | null) => (minutes === null ? EMPTY_VALUE : formatCompactDuration(minutes));
const percentValue = (percent: number | null) => (percent === null ? EMPTY_VALUE : `${Math.round(percent)}%`);

/** Delay and late-completion indicator groups, from the `incidents-times` section. */
export const useTasksTimeMetrics = (
  incidents: IncidentsTimesSectionData | undefined,
  status: SectionStatus,
) => {
  const intl = useIntl();

  return useMemo(() => {
    const toItems = (group: 'delay' | 'late', tone: ReportTone, metrics: MetricDefinition[]): TimeMetricItem[] =>
      metrics.map(metric => {
        const labelKey = group === 'late' && metric.key === 'orders' ? 'rate' : metric.key;
        return {
          key: metric.key,
          label: intl.formatMessage({ id: `dashboards.tasks.summary.${group}.${labelKey}` }),
          value: metric.value,
          detail: intl.formatMessage({ id: `dashboards.tasks.summary.${group}.${labelKey}.detail` }, metric.values),
          tone,
        };
      });

    const delay = incidents ? getDelayMetrics(incidents.delay) : undefined;
    const extraTime = incidents ? getExtraTimeMetrics(incidents.extraTime) : undefined;

    return [
      {
        key: 'delay',
        title: intl.formatMessage({ id: 'dashboards.tasks.summary.delay.title' }),
        description: intl.formatMessage({ id: 'dashboards.tasks.summary.delay.detail' }),
        status,
        items: toItems('delay', 'violet', [
          {
            key: 'orders',
            value: delay?.orders ?? EMPTY_VALUE,
            values: { percent: Math.round(delay?.percent ?? 0) },
          },
          { key: 'average', value: minutesValue(delay?.averageMinutes ?? null) },
          { key: 'total', value: delay ? formatCompactDuration(delay.totalMinutes) : EMPTY_VALUE },
          { key: 'maximum', value: minutesValue(delay?.maxMinutes ?? null) },
        ]),
      },
      {
        key: 'late',
        title: intl.formatMessage({ id: 'dashboards.tasks.summary.late.title' }),
        description: intl.formatMessage({ id: 'dashboards.tasks.summary.late.detail' }),
        status,
        items: toItems('late', 'yellow', [
          {
            key: 'orders',
            value: percentValue(extraTime?.lateRate ?? null),
            values: { count: extraTime?.lateOrders ?? 0, completed: extraTime?.completed ?? 0 },
          },
          { key: 'average', value: minutesValue(extraTime?.averageMinutes ?? null) },
          { key: 'total', value: extraTime ? formatCompactDuration(extraTime.totalMinutes) : EMPTY_VALUE },
          { key: 'maximum', value: minutesValue(extraTime?.maxMinutes ?? null) },
        ]),
      },
    ];
  }, [incidents, intl, status]);
};
